import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CodeMirror, {
  EditorView, Prec, keymap, type ReactCodeMirrorRef,
} from '@uiw/react-codemirror'
import {
  sql, PostgreSQL, SQLite, PLSQL, type SQLNamespace,
} from '@codemirror/lang-sql'
import { oneDark } from '@codemirror/theme-one-dark'
import {
  AlertCircle, Clock, Download, History, Loader2, Play, Table2,
} from 'lucide-react'
import { runSql, type RunSqlResult, type SyncTarget } from '@/api/designers'
import { useThemeStore } from '@/store/themeStore'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import type { LiveCatalog } from './useLiveCatalog'
import ResultsGrid from './ResultsGrid'
import { rowsToCsv, downloadCsv } from './csv'

const LIMITS = [50, 100, 200, 500]
const HISTORY_MAX = 20

const historyKey = (project: string) => `db.sqlHistory.${project}`

function loadHistory(project: string): string[] {
  try {
    const raw = localStorage.getItem(historyKey(project))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : []
  } catch {
    return []
  }
}

interface SqlConsoleViewProps {
  project: string
  target: SyncTarget
  catalog: LiveCatalog
  /**
   * Seed SQL from the Data tab's "Query" button. The nonce makes a repeat
   * click on the same table re-seed the editor rather than silently do
   * nothing — the click is an explicit request for that table's query.
   */
  initialSql?: { sql: string; n: number }
  /** Ref handle so the rail's click-to-insert can reach the editor. */
  insertRef?: React.MutableRefObject<((text: string) => void) | null>
}

/**
 * The SQL console — a read-only SELECT against the project's live database.
 *
 * Drag-and-drop uses NATIVE HTML5 DnD rather than dnd-kit (which the Design
 * view uses for column reorder). CodeMirror handles a `text/plain` drop
 * itself, inserting at the exact character position under the pointer and
 * showing a drop cursor while dragging; dnd-kit simulates dragging with
 * pointer events and never fires a real drop into the editor, so the position
 * would have to be reverse-engineered from coordinates.
 */
export default function SqlConsoleView({
  project, target, catalog, initialSql, insertRef,
}: SqlConsoleViewProps) {
  const { theme } = useThemeStore()
  const editorRef = useRef<ReactCodeMirrorRef>(null)
  const [sqlText, setSqlText] = useState(initialSql?.sql || 'SELECT *\nFROM ')
  const [limit, setLimit] = useState(100)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<RunSqlResult | null>(null)
  const [history, setHistory] = useState<string[]>(() => loadHistory(project))
  const [historyOpen, setHistoryOpen] = useState(false)

  // The latest SQL, readable from the CodeMirror keymap closure (which is
  // built once and would otherwise capture the first render's value).
  const sqlRef = useRef(sqlText)
  useEffect(() => { sqlRef.current = sqlText }, [sqlText])

  const seedNonce = initialSql?.n
  const seedSql = initialSql?.sql
  useEffect(() => {
    if (seedNonce === undefined || !seedSql) return
    setSqlText(seedSql)
    sqlRef.current = seedSql
    // Keyed on the nonce alone: the same SQL seeded twice must still apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedNonce])

  const insertAtCursor = useCallback((text: string) => {
    const view = editorRef.current?.view
    if (!view) {
      setSqlText((prev) => `${prev}${text}`)
      return
    }
    const { from, to } = view.state.selection.main
    view.dispatch({
      changes: { from, to, insert: text },
      selection: { anchor: from + text.length },
    })
    view.focus()
  }, [])

  useEffect(() => {
    if (insertRef) insertRef.current = insertAtCursor
    return () => { if (insertRef) insertRef.current = null }
  }, [insertRef, insertAtCursor])

  const execute = useCallback(async (override?: string) => {
    const statement = (override ?? sqlRef.current).trim()
    if (!statement) return
    setRunning(true)
    try {
      const res = await runSql(project, { sql: statement, target, limit })
      setResult(res)
      if (res.success) {
        setHistory((prev) => {
          const next = [statement, ...prev.filter((s) => s !== statement)]
            .slice(0, HISTORY_MAX)
          try {
            localStorage.setItem(historyKey(project), JSON.stringify(next))
          } catch { /* history is a convenience, never a hard failure */ }
          return next
        })
      }
    } catch (e) {
      setResult({
        success: false,
        message: e instanceof Error ? e.message : 'The query could not be sent.',
      })
    } finally {
      setRunning(false)
    }
  }, [project, target, limit])

  // Run-on-Mod-Enter must beat CodeMirror's default Enter handling, hence Prec.high.
  const runKeymap = useMemo(() => Prec.high(keymap.of([{
    key: 'Mod-Enter',
    run: () => { void execute(); return true },
  }])), [execute])

  // Opening the console loads the whole schema's columns in one request, so
  // autocomplete covers every table — not only the ones already expanded.
  const { loadAllColumns } = catalog
  useEffect(() => { void loadAllColumns() }, [loadAllColumns])

  // Autocomplete schema. The bulk map is authoritative; a table it lacks
  // still completes by NAME, and falls back to any detailed columns already
  // fetched for the rail.
  const schemaNamespace = useMemo<SQLNamespace>(() => {
    const ns: Record<string, string[]> = {}
    for (const t of catalog.tables) {
      const bulk = catalog.columnNames[t.name.toLowerCase()]
      ns[t.name] = bulk
        ?? (catalog.columnsFor(t.name) || []).map((c) => c.column)
    }
    return ns
    // columnsVersion covers the detailed cache — columnsFor is a stable
    // callback over a ref, so it never changes when that cache fills.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.tables, catalog.columnNames, catalog.columnsFor, catalog.columnsVersion])

  const dialect = catalog.dialect === 'oracle' ? PLSQL
    : catalog.dialect === 'sqlite' ? SQLite : PostgreSQL

  const extensions = useMemo(() => [
    sql({ dialect, schema: schemaNamespace, upperCaseKeywords: true }),
    EditorView.lineWrapping,
    runKeymap,
  ], [dialect, schemaNamespace, runKeymap])

  const handleExport = () => {
    if (!result?.columns?.length) return
    downloadCsv('query_result.csv', rowsToCsv(result.columns, result.rows || []))
  }

  const failed = result && !result.success

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
        padding: '0.625rem 1.25rem', borderBottom: '1px solid var(--color-border)',
        flexShrink: 0,
      }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => void execute()}
          disabled={running || !sqlText.trim()}
          title="Run the query (⌘/Ctrl + Enter)"
        >
          {running
            ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            : <Play size={14} />}
          Run
        </button>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          ⌘/Ctrl + ↵
        </span>

        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
        }}>
          Limit
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ fontSize: '0.8125rem', padding: '1px 4px' }}
          >
            {LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          read-only · {target === 'preview' ? 'preview database' : 'deployment database'}
          {catalog.schema ? ` · ${catalog.schema}` : ''}
        </span>

        <div style={{ flex: 1 }} />

        <SqlBuilderButton
          // The unfinished starter text ("SELECT * FROM ") opens a blank build.
          value={/^\s*select\s+\*\s+from\s*$/i.test(sqlText) ? '' : sqlText}
          onChange={setSqlText}
          project={project}
          target={target === 'deployment' ? 'deployment' : 'project'}
          title="SQL console"
        />

        <div style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setHistoryOpen((v) => !v)}
            disabled={!history.length}
            title="Recent queries"
          >
            <History size={14} />
          </button>
          {historyOpen && history.length > 0 && (
            <>
              <div
                onClick={() => setHistoryOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 41,
                width: 420, maxHeight: 320, overflow: 'auto',
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
              }}>
                {history.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSqlText(h)
                      sqlRef.current = h
                      setHistoryOpen(false)
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '6px 10px', fontSize: '0.8125rem',
                      fontFamily: 'var(--font-mono, monospace)',
                      color: 'var(--color-text)', background: 'transparent',
                      border: 'none', borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    title={h}
                  >
                    {h.replace(/\s+/g, ' ')}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Editor */}
      <div style={{
        flexShrink: 0, borderBottom: '1px solid var(--color-border)',
        maxHeight: '38%', overflow: 'auto',
      }}>
        <CodeMirror
          ref={editorRef}
          value={sqlText}
          height="180px"
          theme={theme === 'dark' ? oneDark : undefined}
          extensions={extensions}
          onChange={setSqlText}
          basicSetup={{ lineNumbers: true, foldGutter: false, autocompletion: true }}
          placeholder="SELECT … — drag a table or column from the list on the left"
        />
      </div>

      {/* Status line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        padding: '0.4rem 1.25rem', flexShrink: 0, fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        {result?.success && (
          <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Table2 size={12} />
              {result.row_count?.toLocaleString()} row{result.row_count === 1 ? '' : 's'}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={12} /> {result.elapsed_ms} ms
            </span>
            {result.truncated && (
              <span style={{
                padding: '1px 8px', borderRadius: 999, fontWeight: 600,
                background: 'rgba(245,158,11,0.15)', color: '#f59e0b',
              }}>
                truncated at {limit} — raise the limit to see more
              </span>
            )}
            {!!result.columns?.length && (
              <button
                onClick={handleExport}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--color-accent)', fontSize: '0.8125rem',
                }}
              >
                <Download size={11} /> CSV
              </button>
            )}
          </>
        )}
        {!result && (
          <span>Results appear here. Only SELECT (or WITH … SELECT) can be run.</span>
        )}
      </div>

      {/* Results / error */}
      <div style={{ flex: 1, minHeight: 0, padding: failed ? '1rem 1.25rem' : '0.5rem 0.75rem 0.75rem' }}>
        {failed ? (
          <div style={{
            display: 'flex', gap: '0.625rem', padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
          }}>
            <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)' }}>
                {result?.error === 'not_a_select' ? 'Only SELECT queries are allowed'
                  : result?.error === 'multiple_statements' ? 'One statement at a time'
                  : result?.error === 'forbidden_keyword' ? 'That statement would modify data'
                  : 'Query failed'}
              </div>
              <div style={{
                fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4,
                fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {result?.message}
              </div>
            </div>
          </div>
        ) : (
          <ResultsGrid
            columns={result?.columns || []}
            rows={result?.rows || []}
            emptyMessage={result ? 'The query returned no rows' : 'Run a query to see results'}
          />
        )}
      </div>
    </div>
  )
}
